/** The activity's fact band (plan §2 "Fact band", §3 "Band"): the record fact band
 *  (`records/band/FactBand`, the account band's styling) over the activity's cells.
 *
 *  Each cell leads to what owns its facts: a page tab (a real link) or a card on the record
 *  (a button — `onSelect` scrolls to and focuses it); never off the record — the map is the
 *  Place card's link. A cell whose owner the viewer cannot reach is plain text. Cells come
 *  from `activityFactBandCells`, over the detail payload and the used-by summary. */
import { useMemo } from 'react';

import type { ActivityDetail, ActivityUsedBySummary } from '@/api/activities';
import { FactBand, type FactBandTarget } from '@/features/records/band/FactBand';

import {
  activityFactBandCells,
  type ActivityBandCell,
  type ActivityCardKey,
  type ActivityRecordTab,
} from './activityFactBandModel';

const ALL_TABS: ReadonlySet<ActivityRecordTab> = new Set([
  'activity',
  'images',
  'usage',
  'history',
]);

export interface ActivityFactBandProps {
  detail: ActivityDetail;
  /** The used-by summary (D7, `usedBySummary`), read lazily after the detail; absent → the
   *  cheap counts and the app-reach line only. */
  summary?: ActivityUsedBySummary | null;
  now?: Date;
  /** The tabs the viewer sees: a cell links only to one of them. Default: all. */
  tabs?: ReadonlySet<ActivityRecordTab>;
  hrefFor: (tab: ActivityRecordTab) => string;
  /** Scroll to and focus a card of the record; absent → those cells are plain text. */
  onSelect?: (card: ActivityCardKey) => void;
}

export function ActivityFactBand({
  detail,
  summary = null,
  now,
  tabs = ALL_TABS,
  hrefFor,
  onSelect,
}: ActivityFactBandProps) {
  const cells = useMemo(
    () => activityFactBandCells(detail, { summary, ...(now ? { now } : {}) }),
    [detail, summary, now],
  );
  const targetOf = ({ target: t }: ActivityBandCell): FactBandTarget => {
    if (t.kind === 'tab') return tabs.has(t.tab) ? { to: hrefFor(t.tab) } : null;
    return onSelect ? { onClick: () => onSelect(t.card) } : null;
  };
  return <FactBand label="Activity summary" name="activity" cells={cells} targetOf={targetOf} />;
}
